import styled from 'styled-components';
import { gradient } from '../../../helpers/styleUtils';
import quoteIcon from './images/quote.svg';

const GameSelectorStyled = styled.section`
  background-color: ${props => props.theme.colors.primary};
  opacity: ${props => (props.viewing ? '1' : '0')};
  overflow: hidden;
  transition: transform ${props => props.theme.transition.durationLong}
      ${props => props.theme.transition.easeOut},
    opacity ${props => props.theme.transition.durationLong}
      ${props => props.theme.transition.easeOut};
  transition-delay: ${props => props.theme.transition.durationLong};
  visibility: ${props => (props.viewing ? 'visible' : 'hidden')};
  z-index: ${props => (props.viewing ? '10' : '-1')};

  @media screen and (orientation: landscape) {
    flex-direction: row;
  }
`;

export const GameSelectorWindow = styled.section`
  align-items: flex-start;
  bottom: 0;
  justify-content: stretch;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;

  @media screen and (orientation: landscape) {
    height: ${props => props.theme.gameSelectorWindow.height};
    width: ${props => props.theme.gameSelectorWindow.width};
  }

  @media screen and (min-width: ${props => props.theme.breakpoints.lg}) {
    min-height: ${props => props.theme.gameSelectorWindow.minHeight};
  }
`;

export const GameSelectorList = styled.section`
  background-color: ${props => props.theme.colors.tertiary};
  height: ${props => props.listWidth * 100}vh;
  list-style-type: none;
  margin: 0;
  padding: 0;
  width: ${props => props.theme.gamePanels.portraitWidth};
  transform: translate(0, -${props => props.currentGame * 100}vh);
  transition: transform ${props => props.theme.transition.durationLong}
    ${props => props.theme.transition.easeInOut};

  @media screen and (orientation: landscape) {
    display: flex;
    flex-direction: row;
    height: ${props => props.theme.gamePanels.landscapeHeight};
    transform: translate(-${props => props.currentGame * 100}vw, 0);
    width: ${props => props.listWidth * 100}vw;
  }

  @media screen and (min-width: ${props => props.theme.breakpoints.lg}) {
    min-height: ${props => props.theme.gameSelectorWindow.minHeight};
  }
`;

export const GameSelectorPanel = styled.section`
  align-items: center;
  display: flex;
  flex-direction: column;
  height: ${props => props.theme.gamePanels.portraitHeight};
  justify-content: center;
  overflow: hidden;
  position: relative;
  width: ${props => props.theme.gamePanels.portraitWidth};

  @media screen and (orientation: landscape) {
    height: ${props => props.theme.gamePanels.landscapeHeight};
    width: ${props => props.theme.gamePanels.landscapeWidth};
  }

  @media screen and (min-width: ${props => props.theme.breakpoints.lg}) {
    min-height: ${props => props.theme.gameSelectorWindow.minHeight};
  }
`;

export const GameArticle = styled.article`
  align-items: center;
  display: flex;
  flex-direction: column;
  height: ${props => props.theme.gamePanels.portraitHeight};
  justify-content: space-around;
  width: ${props => props.theme.gamePanels.portraitWidth};
  position: relative;

  &::before {
    ${gradient.background(
      `45deg`,
      props => props.theme.gameThemes[props.gameTheme + 1],
      props => props.theme.gameThemes[props.gameTheme],
      '75%',
      '100%'
    )};
    bottom: 0;
    content: '';
    left: 0;
    opacity: 0.15;
    position: absolute;
    right: 0;
    top: 0;
    z-index: -1;
  }

  @media screen and (orientation: landscape) {
    height: ${props => props.theme.gamePanels.landscapeHeight};
    width: ${props => props.theme.gamePanels.landscapeWidth};
  }

  @media screen and (min-width: ${props => props.theme.breakpoints.lg}) {
    min-height: ${props => props.theme.gameSelectorWindow.minHeight};
  }
`;

export const GameArticleIndent = styled.section`
  background: ${props => props.theme.colors.tertiary};
  border-radius: 10px;
  box-shadow: 0 10px 10px rgba(0, 0, 0, 0.1);
  color: ${props => props.theme.colors.tertiary};
  margin: 5vh 0;
  max-width: 160px;
  padding: calc(40px + 1vmin) 20px;
  position: relative;
  width: 100%;

  &::before {
    background: ${props => props.theme.colors.white};
    bottom: 0;
    border-radius: 10px;
    content: '';
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
    transform: scaleY(0);
    transform-origin: 50% 100%;
    transition: transform ${props => props.theme.transition.durationLong}
      ${props => props.theme.transition.easeInOut};

    .game-end-start & {
      transform: scaleY(0);
      transform-origin: 50% 0;
    }

    .game-enter-start &,
    .game-enter-done & {
      transform: scaleY(1);
    }
  }

  @media screen and (min-width: ${props => props.theme.breakpoints.sm}) {
    max-width: 180px;
  }

  @media screen and (min-width: ${props => props.theme.breakpoints.md}) {
    max-width: 200px;
  }

  @media screen and (min-width: ${props => props.theme.breakpoints.lg}) {
    min-height: calc(190px + 1vmin);
    min-width: calc(170px + 1vmin);
    margin: 5vh 5vw;
    max-width: 250px;
    padding: 8vmin 20px;
    transition: border-color 2s ${props => props.theme.transition.easeOut};
    transition-delay: ${props => props.theme.transition.duration};

    .game-enter-done & {
      opacity: 1;
      transform: translateX(0) skew(0deg);
    }
  }
`;

export const GameArticleIntro = styled.h2`
  color: ${props => props.theme.colors.tertiary};
  font-size: calc(${props => props.theme.fonts.baseSize} + 0.85vmin);
  font-weight: 600;
  line-height: 1.4;
  margin: 40px 20px 30px;
  opacity: 0;
  position: relative;
  text-align: center;
  text-transform: uppercase;
  transition: opacity ${props => props.theme.transition.durationLong}
    ${props => props.theme.transition.easeOut};
  transition-delay: 0.7s;
  z-index: 2;

  @media screen and (min-width: ${props => props.theme.breakpoints.md}) {
    margin: 40px 30px 30px;
  }

  &::before {
    background-size: 60%;
    background-color: rgba(0, 0, 0, 0.95);
    background-image: url(${quoteIcon});
    background-repeat: no-repeat;
    background-position: center;
    border-radius: 100%;
    content: '';
    height: 15px;
    left: 50%;
    opacity: 0.2;
    position: absolute;
    transform: translate(-50%, -30px);
    width: 15px;
  }

  span:nth-child(even) {
    position: relative;
    top: -2px;
  }

  .game-enter-done & {
    opacity: 1;
  }
`;

export const GameDefList = styled.dl`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 0 10px;
  outline: 0;
  position: relative;
  z-index: 2;
`;

export const GameDT = styled.dt`
  color: ${props => props.theme.colors.secondary};
  font-size: calc(${props => props.theme.fonts.baseSize} + 0.55vmin);
  margin: 0 5px;
  opacity: 0;
  text-align: right;
  transition: opacity ${props => props.theme.transition.durationLong}
    ${props => props.theme.transition.easeOut};
  transition-delay: 0.8s;

  .game-enter-done & {
    opacity: 1;
  }
`;

export const GameDD = styled.dd`
  color: ${props => props.theme.colors.tertiary};
  font-size: calc(${props => props.theme.fonts.baseSize} + 0.55vmin);
  font-weight: 800;
  margin: 0 5px;
  opacity: 0;
  position: relative;
  text-align: left;
  transition: opacity ${props => props.theme.transition.durationLong}
    ${props => props.theme.transition.easeOut};
  transition-delay: 0.9s;

  small {
    font-size: 0.7rem;
    font-weight: 600;
    margin-left: 0.1rem;
  }

  .game-enter-done & {
    opacity: 1;
  }
`;

export default GameSelectorStyled;



// WEBPACK FOOTER //
// ./src/components/organisms/GameSelector/GameSelector.styled.js